import { GameObjectManager } from "./plan/GameObjectManager";
import { FrontViewManager } from "./plan/FrontViewManager";
import { EntityNode } from "./plan/EntityNodes/EntityNode";
import { PlanManager } from "../../cocosPlan";
import { Coating, ComponentConstants, Entity, Events, eventsManager, math, roomManager, SavaneConstants, Wall } from "@rhinov/savane-js";

declare var cc: any;
declare var $: any;

export class FrontViewLayer extends cc.Layer {
    currentWall: Wall | null = null;
    touches: number[] = [];
    node: EntityNode | null = null;
    isDirectSide: boolean = true;
    rectangle: any = null;
    _scale: number | null = null;
    _uiScale: number = 1;
    downTime: Date | null = null;
    lastClickTime: Date | null = null;
    _length: number = 0;
    _previousPosition: any = new cc.p();

    constructor() {
        super(new cc.color(255, 255, 255, 255));
        this.node = new cc.DrawNode();
        this.addChild(this.node);

        this.addListeners();
    }

    toggleSide() {
        this.isDirectSide = !this.isDirectSide;
        PlanManager.getInstance().mouseClickCancelled();
        this._uiScale = 1;
        this._scale = null;
        this.computeWallLength();
        this.renderCurrentWall();
    }

    computeWallLength(): number {
        //compute wall length extended by adjacent wall thickness
        var bbox = this.currentWall!.boundingBoxCut;
        var d = math.vec3.create();
        if (this.isDirectSide) {
            math.vec3.subtract(d, bbox[2], bbox[1]);
        } else {
            math.vec3.subtract(d, bbox[3], bbox[0]);
        }
        this._length = math.vec3.length(d);
        return this._length;
    }

    computeFloorHeight(): number {
        this.floorHeight = Number.MAX_VALUE;
        if (this.currentWall!.rooms.length === 0) {
            var room = roomManager.getRoomAtPosition(this.currentWall!.center, this.currentWall!.floor!);
            if (room) {
                this.floorHeight = room.floorHeight;
            } else {
                this.floorHeight = 0;
            }
        }
        for (var i = 0; i < this.currentWall!.rooms.length; ++i) {
            var currentRoom = this.currentWall!.rooms[i];
            this.floorHeight = Math.min(this.floorHeight, currentRoom.floorHeight);
        }
        return this.floorHeight;
    }

    setCurrentWall(wall: Wall) {
        this._uiScale = 1;
        this.currentWall = wall;
        if (wall.rooms.length > 1) {
            this.isDirectSide = true;
        }
        else {
            this.isDirectSide = (wall.getHangTypeForRoom(wall.rooms[0]) === Coating.HangType.wallDirect);
        }

        this.computeFloorHeight();
        this.computeWallLength();
        this.renderCurrentWall();
    }

    renderCurrentWall(update?: boolean) {
        if (!this.currentWall) {
            return;
        }

        this.node!.clear();
        if (!update) {
            this.clearDecorations();
        }

        if (this.rectangle !== null) {
            //recompute origin and scale
            if (this._scale === null || this._origin === null) {
                this._scale = null;

                if (this._length / this.rectangle.width > this.currentWall!.height / this.rectangle.height) {
                    this._scale = this.rectangle.width / this._length;
                    var offset = (this.rectangle.height - this.currentWall!.height * this._scale) / 2;
                    this._origin = new cc.p(this.rectangle.x, this.rectangle.y + offset);
                }
                else {
                    this._scale = this.rectangle.height / this.currentWall!.height;
                    var offset = (this.rectangle.width - this._length * this._scale) / 2;
                    this._origin = new cc.p(this.rectangle.x + offset, this.rectangle.y);
                }
            }
            this.node!.setPosition(this._origin);

            // Get wall coating color
            var wallColor = '#FFFFFF';

            var coatings = this.currentWall!.getComponents(ComponentConstants.ComponentType.Coating) as Coating[];

            if (coatings !== null) {
                for (var i = 0; i < coatings.length; i++) {
                    if ((coatings[i].hangType === Coating.HangType.wallDirect && this.isDirectSide) ||
                        (coatings[i].hangType === Coating.HangType.wallUndirect && !this.isDirectSide)) {
                        if (coatings[i].colors[0] && coatings[i].colors[0].realColor !== undefined) {
                            wallColor = coatings[i].colors[0].realColor;
                        }
                        else {
                            wallColor = new cc.color(255, 0, 255);
                        }
                    }
                }
            }

            this.wallRec = new cc.rect(0, this.floorHeight * this._scale!, this._length * this._scale!, (this.currentWall!.height - this.floorHeight) * this._scale!);
            (FrontViewManager as any).wallBox = this.wallRec;
            this.node!.drawRect(
                new cc.p(this.wallRec.x, this.wallRec.y),
                new cc.p(this.wallRec.x + this.wallRec.width, this.wallRec.y + this.wallRec.height),
                new cc.color(wallColor), 4, new cc.color('#C8CACC'));

            if (this.currentWall!.slope) {
                var yellow = new cc.color('#F5D231');
                yellow.a = 90;

                if (this.isDirectSide) {
                    if (this.currentWall!.slopeLength1 !== 0) {
                        this.node!.drawRect(
                            new cc.p(this.wallRec.x, this.wallRec.y + this.currentWall!.slopeHeight * this._scale!),
                            new cc.p(this.wallRec.x + this.wallRec.width, this.wallRec.y + this.wallRec.height),
                            yellow, 4, new cc.color('#C8CACC'));
                    }
                }
                else {
                    if (this.currentWall!.slopeLength2 !== 0) {
                        this.node!.drawRect(
                            new cc.p(this.wallRec.x, this.wallRec.y + this.currentWall!.slopeHeight * this._scale!),
                            new cc.p(this.wallRec.x + this.wallRec.width, this.wallRec.y + this.wallRec.height),
                            yellow, 4, new cc.color('#C8CACC'));
                    }
                }
            }

            this.renderWallDimension();
            this.renderDecorations();
        }
    }

    resize(width: number, height: number) {
        this.width = width;
        this.height = height;
        this._uiScale = 1;
        this._scale = null;
        this.rectangle = new cc.rect(100, 100, this.width - 200, this.height - 200);

        this.renderCurrentWall();
    }

    getZoomScale(): number {
        return this._uiScale;
    }

    setVisible(bool: boolean) {
        super.setVisible(bool);
        if (!bool) {
            this.removeAllChildren(true);
            this.node = new cc.DrawNode();
            this.addChild(this.node);
        }
    }

    renderDecorations() {
        if (this.currentWall !== null) {
            var floor = PlanManager.getInstance().world.currentScene!.currentFloor!;
            var wallDecorations = floor.getWallDecorations(this.currentWall.id, this.isDirectSide, 5 * SavaneConstants.PositionTolerance);
            var wallTechElements = floor.getWallTechnicalElements(this.currentWall.id, this.isDirectSide, 5 * SavaneConstants.PositionTolerance);
            var areaCoatings = this.currentWall.getComponents(ComponentConstants.ComponentType.CoatingArea);
            var cutAreas = this.currentWall.getComponents(ComponentConstants.ComponentType.CutArea);
            var addAreas = this.currentWall.getComponents(ComponentConstants.ComponentType.AddArea);
            for (var i = 0; i < this.currentWall.children.length; ++i) {
                FrontViewManager.updateDecoration(this.currentWall.children[i]);
            }
            for (i = 0; i < wallDecorations.length; ++i) {
                FrontViewManager.updateDecoration(wallDecorations[i]);
            }
            for (i = 0; i < wallTechElements.length; ++i) {
                FrontViewManager.updateDecoration(wallTechElements[i]);
            }
            for (i = 0; i < areaCoatings.length; ++i) {
                FrontViewManager.drawArea(areaCoatings[i], false, 0, null, null);
            }
            for (i = 0; i < cutAreas.length; ++i) {
                FrontViewManager.drawArea(cutAreas[i], false, 0, null, null);
            }
            for (i = 0; i < addAreas.length; ++i) {
                FrontViewManager.drawArea(addAreas[i], false, 0, null, null);
            }
        }
    }

    clearDecorations() {
        var decos = this.node!.getChildren();
        for (var i = decos.length - 1; i >= 0; --i) {
            decos[i].removeFromParent();
        }
    }

    deleteDeco(deco: Entity) {
        FrontViewManager.deleteDeco(deco);
    }

    renderWallDimension() {
        var nodePosition = this.node!.getPosition();

        GameObjectManager.createDimension('wallDimTopDeco', '',
            new cc.p(nodePosition.x, nodePosition.y + this.wallRec.height + this.wallRec.y),
            new cc.p(nodePosition.x + this.wallRec.width, nodePosition.y + this.wallRec.height + this.wallRec.y),
            new cc.p(0, 1), -20, this._scale! * 10, true);
        GameObjectManager.createDimension('wallDimLeftDeco', '',
            new cc.p(nodePosition.x, nodePosition.y + this.wallRec.y),
            new cc.p(nodePosition.x, nodePosition.y + this.wallRec.height + this.wallRec.y),
            new cc.p(1, 0), 20, this._scale! * 10, true);
    }

    convertToScene(pos: {x: number, y: number}): math.vec3 {
        var point = math.vec3.create();
        math.vec3.set(point, (pos.x - this._origin!.x) / this._scale!, 0, (pos.y - this._origin!.y) / this._scale!);
        return point;
    }

    addListeners() {
        this.ctrl = false;
        this.shift = false;
        cc.eventManager.addListener({
            event: cc.EventListener.MOUSE,
            swallowTouches: false,
            onMouseUp: ((event: any) => {
                if (!this.isVisible() || this._origin === null || this._origin === undefined) {
                    return;
                }
                if (this.downTime === null)
                    return false;
                var dateNow = new Date();
                if (this.downTime === null)
                    return false;
                var elapsedTime = dateNow.getTime() - this.downTime.getTime();

                var target = event.getCurrentTarget();
                target.mouse_x = event.getLocationX();
                target.mouse_y = event.getLocationY();
                var pos = new cc.p(event.getLocationX(), event.getLocationY());

                var width = ($('#gameCanvas') as any).width();
                var height = ($('#gameCanvas') as any).height();
                if (target.mouse_x < 0 || target.mouse_x > width ||
                    target.mouse_y < 0 || target.mouse_y > height) {
                    return;
                }

                PlanManager.getInstance().mouseClickUp(this.convertToScene(pos), elapsedTime);
                eventsManager.instance.dispatch(Events.START_UPDATING_ENVS);
            }),
            onMouseDown: ((event: any) => {
                if (!this.isVisible() || this._origin === null || this._origin === undefined) {
                    return;
                }
                eventsManager.instance.dispatch(Events.STOP_UPDATING_ENVS);
                var button = event.getButton();
                //Save mouse button pressed to handle behaviour for each button
                PlanManager.getInstance().mouseButton = button;

                this.downTime = new Date();

                var curDate = new Date();
                var isDoubleClick = false;
                if (this.lastClickTime !== null) {
                    isDoubleClick = (curDate.getTime() - this.lastClickTime.getTime()) < 300;
                }

                this.lastClickTime = new Date();

                switch (button) {
                    case cc.EventMouse.BUTTON_RIGHT:
                        PlanManager.getInstance().mouseClickCancelled();
                        break;
                    case cc.EventMouse.BUTTON_LEFT:
                        var target = event.getCurrentTarget();
                        target.mouse_x = event.getLocationX();
                        target.mouse_y = event.getLocationY();
                        var pos = new cc.p(event.getLocationX(), event.getLocationY());
                        PlanManager.getInstance().mouseClickDown(this.convertToScene(pos), isDoubleClick, button);
                        break;
                    case cc.EventMouse.BUTTON_MIDDLE:
                        this._previousPosition.x = event.getLocationX();
                        this._previousPosition.y = event.getLocationY();
                        break;
                }

                return true;
            }),
            onMouseMove: ((event: any) => {
                if (!this.isVisible() || this._origin === null || this._origin === undefined) {
                    return;
                }

                eventsManager.instance.dispatch(Events.STOP_UPDATING_ENVS);
                switch (PlanManager.getInstance().mouseButton) {
                    case cc.EventMouse.BUTTON_MIDDLE:
                        this._origin.x += (event.getLocationX() - this._previousPosition.x);
                        this._origin.y += (event.getLocationY() - this._previousPosition.y);
                        this._previousPosition.x = event.getLocationX();
                        this._previousPosition.y = event.getLocationY();
                        this.renderCurrentWall(true);
                        eventsManager.instance.dispatch(Events.FRONT_VIEW_MOUSE_MOVED);
                        break;
                    default:
                        var target = event.getCurrentTarget();
                        target.mouse_x = event.getLocationX();
                        target.mouse_y = event.getLocationY();
                        var pos = new cc.p(event.getLocationX(), event.getLocationY());
                        PlanManager.getInstance().moveMouseCursor(this.convertToScene(pos));
                        break;
                }
            }),
            onMouseScroll: ((event: any) => {
                if (!this.isVisible() || this._origin === null || this._origin === undefined) {
                    return;
                }

                eventsManager.instance.dispatch(Events.STOP_UPDATING_ENVS);
                var target = event.getCurrentTarget();
                target.mouse_x = event.getLocationX();
                target.mouse_y = event.getLocationY();

                var val = Math.abs(event.getScrollY());
                if (val === 0) {
                    val = 1;
                }

                var delta = Math.ceil(event.getScrollY() / val) / 100;
                this._scale! += delta;
                this._uiScale += delta;

                var dx = (this._length / 2) * delta;
                var dy = (this.currentWall!.height / 2) * delta;
                this._origin.x -= dx;
                this._origin.y -= dy;

                this.renderCurrentWall(true);
                eventsManager.instance.dispatch(Events.FRONT_VIEW_MOUSE_MOVED);
            })
        }, this);
    }
}